import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { toLocaleString } from "@circle-react/helpers/number";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { usePostConversationSummary } from "@circle-react/hooks/posts/usePostConversationSummary";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import type { Post } from "@circle-react/types/Post";
import { useCallback, useState } from "react";

import { t } from "@/i18n-js/instance";
import { AiSummary } from "@/react/components/shared/AiSummary";

import { ConversationSummaryV4 } from "./ConversationSummaryV4";
import { DisabledNotice } from "./DisabledNotice";

interface ConversationSummaryProps {
  className?: string;
  post: Post;
}

const LOCALE_ROOT = "post.conversation_summary";

export const ConversationSummary = ({
  post,
  className,
}: ConversationSummaryProps) => {
  const { isCircleV4 } = useCircleV4();

  if (isCircleV4) {
    return <ConversationSummaryV4 post={post} className={className} />;
  }

  return <ConversationSummaryV3 post={post} className={className} />;
};

const ConversationSummaryV3 = ({
  post,
  className,
}: ConversationSummaryProps) => {
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const {
    conversationSummary,
    refetchConversationSummary,
    clearConversationSummary,
    updateConversationSummary,
    isSaving,
  } = usePostConversationSummary({ post });
  const [isExpanding, setIsExpanding] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleExpandClick = () => {
    setIsExpanding(true);
    void refetchConversationSummary();
  };

  const handleCollapseClick = () => {
    setIsExpanding(false);
    clearConversationSummary();
  };

  const handleExpandComplete = useCallback(() => {
    setIsExpanding(false);
  }, []);

  const handleDisableClick = async () => {
    await updateConversationSummary({ disabled: true });
    setIsDisabled(true);
  };

  const handleUndoClick = async () => {
    await updateConversationSummary({ disabled: false });
    setIsDisabled(false);
  };

  const isCollapsed = !isExpanding && !conversationSummary;
  const isExpandCompleted = !isExpanding && Boolean(conversationSummary);

  if (!post.conversation_summary_overview) {
    return null;
  }

  if (isDisabled && isAdmin) {
    return (
      <div className={classNames("py-5 md:px-6", className)}>
        <DisabledNotice onUndo={handleUndoClick} disabled={isSaving} />
      </div>
    );
  }

  return (
    <div className={classNames("flex py-5 md:px-6", className)}>
      <AiSummary
        isCollapsed={isCollapsed}
        onCollapseClick={handleCollapseClick}
        onExpandClick={handleExpandClick}
        className="pb-4"
      >
        <AiSummary.Content closeButtonPosition="sidebar">
          <div className="flex items-center justify-between">
            <AiSummary.Header
              animate={isExpanding}
              isHidden={isExpandCompleted}
            >
              {t([LOCALE_ROOT, isCollapsed ? "title" : "loading"])}
            </AiSummary.Header>
            {isAdmin && isCollapsed && (
              <Dropdown
                direction="bottom-end"
                menuButtonEl="div"
                button={
                  <IconButton
                    name="20-dot-menu"
                    iconClassName="!text-default group-hover:!text-dark"
                    className="group"
                    variant="secondary"
                    iconSize={20}
                    buttonSize={24}
                    ariaLabel={t([LOCALE_ROOT, "actions", "title"])}
                    disabled={isSaving}
                  />
                }
                className="z-10"
                transitionWrapperClassName="z-50"
                size="large"
                buttonLabel={t([LOCALE_ROOT, "actions", "title"])}
              >
                <Dropdown.ItemLink onClick={handleDisableClick} hasDanger>
                  {t([LOCALE_ROOT, "actions", "disable"])}
                </Dropdown.ItemLink>
              </Dropdown>
            )}
          </div>
          {isCollapsed && (
            <AiSummary.BodyText>
              {post.conversation_summary_overview}
            </AiSummary.BodyText>
          )}
          {conversationSummary?.content && (
            <AiSummary.BodyText
              animation="fade-in"
              onAnimationComplete={handleExpandComplete}
            >
              {conversationSummary.content}
            </AiSummary.BodyText>
          )}
        </AiSummary.Content>
        {post.comments_count > 0 && isExpandCompleted && (
          <AiSummary.Footer>
            <AiSummary.FooterNote>
              {t([LOCALE_ROOT, "comments_summarized"], {
                count: post.comments_count,
                formatted_count: toLocaleString(post.comments_count),
              })}
            </AiSummary.FooterNote>
          </AiSummary.Footer>
        )}
      </AiSummary>
    </div>
  );
};
